import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { Drawer } from '@/components/ui/Drawer';
import { getSeoAutopilot } from '@/data/seo-autopilot';
import { Button, EngineKeyline, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useContentLibraryFlag } from '@/hooks/useContentLibraryFlag';
import { fetchLibrary, type LibraryItem, type LibrarySocialItem } from '@/data/sources-library';
import { approveContent, deleteContent, rejectContent, submitReview } from '@/data/sources-social';
import { deleteBlogDraft } from '@/data/sources-content';
import { SeoAutopilotPanel } from '@/pages/social/SeoAutopilotPanel';
import {
  DEFAULT_FILTERS, activeFilterCount, apiQuery, bulkTargets, effectiveView, filtersFromParams, filtersToParams, itemKey, todayParts,
  type BulkAction, type BulkItem, type LibraryFilters, type LibraryView,
} from './libraryModel';
import { ActiveChips, FilterControls, FilterSheet, SearchBox, ViewChips } from './LibraryFilterBar';
import { LibraryCards, LibraryTable, type RowHandlers } from './LibraryRows';
import { useWidth } from './useWidth';
import { LibraryPreviewDrawer, type DrawerActions } from './LibraryPreviewDrawer';
import { BlogEditorDialog, BulkResults, ConfirmDeleteDialog, RequestChangesDialog, type BulkOutcome } from './LibraryDialogs';

/* ----------------------------------------------------------------------------
 * Content Library console (lane bc-content-studio-ux; owner GO 24-10-2026,
 * decisions D1-D8 "yes go with all recommended"). One list of blogs + social
 * posts, sticky filters and saved views with counts, table/cards, preview
 * drawer with the existing actions, URL-persisted state and server paging.
 *
 * Domain isolation rules (P0 Content Studio) are kept: the default scope is the
 * shell's Viewing domain (query key includes it, so switching refetches and
 * drops stale rows); "All domains" and "Unassigned" are explicit lenses that
 * label each row; opening a social post pins the shell to the row's OWN domain
 * (rule 4). Every mutation calls the existing per-item endpoint and reports the
 * server's real result; there is no bulk publish and no new write path.
 * -------------------------------------------------------------------------- */

const LIMIT = 50;

function errText(e: unknown, fallback: string): string {
  if (e instanceof ApiError && (e.status === 401 || e.status === 403)) return 'Your session has expired or you are not permitted to do this. Refresh and try again.';
  return fallback;
}

/** Route guard: flag OFF (or unknown) -> today's studio, unchanged. */
export function ContentLibraryRoute() {
  const flag = useContentLibraryFlag();
  if (flag.isLoading) return <LoadingState rows={4} />;
  if (!flag.enabled) return <Navigate to="/social/content-studio" replace />;
  return <ContentLibraryPage />;
}

export function ContentLibraryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(sp), [sp]);
  const { active, select } = useActiveDomain();
  const projectId = active?.projectId ?? null;
  const siteLabel = active ? active.displayName || active.hostname || '' : '';
  const wide = useMediaQuery('(min-width: 761px)');
  const todayKey = new Date().toDateString();
  const today = useMemo(() => todayParts(), [todayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const [needsReviewSeen, setNeedsReviewSeen] = useState<number | null>(null);
  const view = effectiveView(filters, needsReviewSeen);

  const update = useCallback((patch: Partial<LibraryFilters>, replace = false) => {
    // UI-C2: "Push failed" items live in the Failed view, so choosing that state goes there.
    const next = { ...filters, ...patch };
    if (patch.wp && patch.wp.includes('failed') && next.view !== 'failed') next.view = 'failed';
    setSp(filtersToParams(next), { replace });
  }, [filters, setSp]);

  const baseQuery = apiQuery(filters, view, projectId, today, null, LIMIT);
  const noDomain = filters.scope === 'domain' && projectId == null;
  const queryKey = ['content-library', baseQuery] as const;
  const list = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) => fetchLibrary(apiQuery(filters, view, projectId, today, pageParam, LIMIT), signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !noDomain,
  });
  const first = list.data?.pages[0];
  useEffect(() => {
    if (filters.view === null && first && needsReviewSeen === null) setNeedsReviewSeen(first.facets.needs_review);
  }, [filters.view, first, needsReviewSeen]);

  const items: LibraryItem[] = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);

  // ---- selection ------------------------------------------------------------
  const [selected, setSelected] = useState<Map<string, BulkItem & { title: string }>>(new Map());
  useEffect(() => { setSelected(new Map()); }, [sp, projectId]);
  const toggle = (it: LibraryItem) => setSelected((m) => {
    const n = new Map(m); const k = itemKey(it);
    if (n.has(k)) n.delete(k); else n.set(k, { kind: it.kind, id: it.id, status: it.status, title: it.title });
    return n;
  });
  const allSelected = items.length > 0 && items.every((i) => selected.has(itemKey(i)));
  const toggleAll = () => setSelected(() => {
    if (allSelected) return new Map();
    const n = new Map<string, BulkItem & { title: string }>();
    for (const it of items) n.set(itemKey(it), { kind: it.kind, id: it.id, status: it.status, title: it.title });
    return n;
  });

  // ---- actions (existing endpoints only) --------------------------------------
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const preview = useMemo(() => {
    if (!previewKey) return null;
    for (const it of items) {
      if (itemKey(it) === previewKey) return it;
      if (it.kind === 'blog') for (const c of it.children) if (itemKey(c) === previewKey) return c;
    }
    return null;
  }, [previewKey, items]);
  const [busy, setBusy] = useState(false);
  const [drawerMsg, setDrawerMsg] = useState<string | null>(null);
  const [blogOpen, setBlogOpen] = useState<{ id: number; projectId: number | null } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Array<BulkItem & { title: string }> | null>(null);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Array<BulkItem & { title: string }> | null>(null);
  const [bulk, setBulk] = useState<{ label: string; results: BulkOutcome[] } | null>(null);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['content-library'] });
    void queryClient.invalidateQueries({ queryKey: ['social-content'] });
    void queryClient.invalidateQueries({ queryKey: ['content-blog-drafts'] });
  }, [queryClient]);

  const openItem = (it: LibraryItem) => {
    if (it.kind === 'blog') {
      if (it.status !== 'ready') { setPreviewKey(itemKey(it)); return; }
      setBlogOpen({ id: it.id, projectId: it.project_id ?? projectId });
      return;
    }
    // Rule 4: open under the post's OWN domain.
    const pid = it.project_id;
    if (pid && pid > 0 && pid !== projectId) void select(pid);
    navigate('/social/content-studio/' + it.id);
  };

  const runOne = async (it: BulkItem, action: BulkAction, note = ''): Promise<void> => {
    if (action === 'delete') { if (it.kind === 'blog') await deleteBlogDraft(it.id); else await deleteContent(it.id); return; }
    if (action === 'approve') { await approveContent(it.id); return; }
    if (action === 'request_changes') { await rejectContent(it.id, note); return; }
    if (action === 'submit_review') { await submitReview(it.id); }
  };

  const LABEL: Record<BulkAction, string> = { approve: 'Approve', request_changes: 'Request changes', submit_review: 'Submit for review', delete: 'Delete' };
  const runBulk = async (targets: Array<BulkItem & { title: string }>, action: BulkAction, note = '') => {
    setBusy(true);
    const results: BulkOutcome[] = [];
    for (const t of targets) { // one by one through the existing per-item endpoint
      try { await runOne(t, action, note); results.push({ key: itemKey(t), title: t.title, ok: true, message: '' }); }
      catch (e) { results.push({ key: itemKey(t), title: t.title, ok: false, message: errText(e, e instanceof ApiError ? e.message : 'Not done. Try again.') }); }
    }
    setBusy(false);
    setSelected(new Map());
    refresh();
    return results;
  };

  const single = async (it: LibrarySocialItem, action: BulkAction, note = '') => {
    setDrawerMsg(null);
    const [r] = await runBulk([{ kind: it.kind, id: it.id, status: it.status, title: it.title }], action, note);
    setDrawerMsg(r.ok ? `${LABEL[action]}: done.` : `${LABEL[action]}: not done. ${r.message}`);
  };

  const drawerActions: DrawerActions = {
    open: openItem,
    remove: (it) => { setDeleteErr(null); setPendingDelete([{ kind: it.kind, id: it.id, status: it.status, title: it.title }]); },
    approve: (it) => void single(it, 'approve'),
    requestChanges: (it) => setPendingChanges([{ kind: it.kind, id: it.id, status: it.status, title: it.title }]),
    submit: (it) => void single(it, 'submit_review'),
  };

  const handlers: RowHandlers = { selected: new Set(selected.keys()), toggle, preview: (it) => { setDrawerMsg(null); setPreviewKey(itemKey(it)); }, open: openItem };
  const sel = Array.from(selected.values());
  const can = (a: BulkAction) => bulkTargets(sel, a).length;

  const [sheet, setSheet] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [autopilotOpen, setAutopilotOpen] = useState(false);
  // Owner 25-10-2026: make the SEO autopilot findable from the Library (entry + status chip).
  const autopilot = useQuery({ queryKey: ['seo-autopilot'], queryFn: getSeoAutopilot, retry: false, staleTime: 60_000 });
  const [barRef, barWidth] = useWidth<HTMLDivElement>();
  // UI-C1: only the views + search stay sticky. Below ~1000px of available width
  // (e.g. 1366 with the Copilot rail open) the other filters sit behind "More filters".
  const roomy = barWidth === 0 || barWidth >= 1000;
  const showControls = wide && (roomy || moreOpen);
  const layout = !wide ? 'cards' : filters.layout === 'cards' ? 'cards' : 'table';
  const fcount = activeFilterCount(filters);
  const options = first?.options ?? { target_pages: [], keywords: [], channels: [] };
  const showHidden = !!first && view !== 'failed' && view !== 'empty' && (first.hidden.failed > 0 || first.hidden.empty > 0);

  const subtitle = filters.scope === 'all' ? 'Every blog and social post across your workspace, each labelled with its domain.'
    : filters.scope === 'unassigned' ? 'Blogs and posts not yet assigned to a domain.'
      : siteLabel ? `Every blog and social post for ${siteLabel}. Filter, review and open anything in one place.`
        : 'Choose a domain to view at the top of the app, or switch Site to All domains.';

  return (
    <div className="mx-auto w-full min-w-0 max-w-6xl">
      <PageHeader title="Content library" subtitle={subtitle}
        actions={
          <>
            {!(autopilot.error instanceof ApiError && autopilot.error.status === 403) && (
              <Button variant="secondary" className="min-h-[44px]" onClick={() => setAutopilotOpen(true)} aria-label="Open SEO autopilot">
                SEO autopilot
                {autopilot.data && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-border-default px-1.5 py-0.5 text-[11px] font-semibold text-text-primary">
                    <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', autopilot.data.running ? 'bg-success' : 'bg-warning')} />
                    {autopilot.data.running ? 'Running' : 'Stopped'}{autopilot.data.alerts.length ? ` · ${autopilot.data.alerts.length} to check` : ''}
                  </span>
                )}
                {autopilot.isError && <span className="text-[11px] text-text-secondary">unavailable</span>}
              </Button>
            )}
            <Button variant="ai" className="min-h-[44px]" onClick={() => navigate('/social/content-studio')}>+ Create</Button>
          </>
        } />
      <EngineKeyline className="mb-3" />

      <div ref={barRef} className="sticky top-0 z-20 -mx-4 border-b border-border-subtle bg-canvas/95 px-4 pb-2 pt-2 backdrop-blur lg:-mx-6 lg:px-6">
        <ViewChips view={view} facets={first ? first.facets : null} onPick={(v: LibraryView) => update({ view: v })} />
        <div className="mt-2 flex min-w-0 items-end gap-2">
          <SearchBox value={filters.q} onChange={(q) => update({ q }, true)} />
          {!wide ? (
            <button onClick={() => setSheet(true)} className="h-11 shrink-0 rounded-md border border-border-default bg-surface px-3 text-sm font-medium text-text-primary">
              Filters{fcount ? ` (${fcount})` : ''}
            </button>
          ) : (
            <>
            {!roomy && (
              <button onClick={() => setMoreOpen((o) => !o)} aria-expanded={moreOpen} aria-controls="library-more-filters"
                className="h-9 shrink-0 rounded-md border border-border-default bg-surface px-3 text-sm font-medium text-text-primary">
                {moreOpen ? 'Fewer filters' : 'More filters'}{fcount ? ` (${fcount})` : ''}
              </button>
            )}
            <div role="group" aria-label="Layout" className="inline-flex shrink-0 rounded-md border border-border-default bg-surface p-0.5">
              {(['table', 'cards'] as const).map((l) => (
                <button key={l} aria-pressed={layout === l} onClick={() => update({ layout: l === 'table' ? 'auto' : 'cards' })}
                  className={cn('h-8 rounded px-3 text-xs', layout === l ? 'bg-ai/10 font-semibold text-ai' : 'text-text-secondary')}>
                  {l === 'table' ? 'Table' : 'Cards'}
                </button>
              ))}
            </div>
            </>
          )}
        </div>
      </div>
      {showControls && <div id="library-more-filters" className="mt-2"><FilterControls f={filters} set={(p) => update(p)} options={options} today={today} /></div>}
      <ActiveChips f={filters} set={(p) => update(p)} clearAll={() => setSp(filtersToParams({ ...DEFAULT_FILTERS, view: filters.view, layout: filters.layout }))} total={first ? first.total : null} />
      {showHidden && first && (
        <p className="mt-1 text-xs text-text-secondary">
          Hidden here:{' '}
          <button className="inline-flex min-h-[44px] items-center font-medium text-ai underline sm:min-h-0" onClick={() => update({ view: 'failed' })}>{first.hidden.failed} failed</button>,{' '}
          <button className="inline-flex min-h-[44px] items-center font-medium text-ai underline sm:min-h-0" onClick={() => update({ view: 'empty' })}>{first.hidden.empty} empty</button>
          {' '}— open those views to see them.
        </p>
      )}

      <FilterSheet open={sheet} onClose={() => setSheet(false)} count={fcount}>
        <FilterControls f={filters} set={(p) => update(p)} options={options} today={today} stacked />
      </FilterSheet>

      {sel.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-border-default bg-inset px-3 py-2 text-xs">
          <span className="font-semibold text-text-primary">{sel.length} selected</span>
          <Button variant="secondary" disabled={busy || !can('submit_review')} onClick={async () => setBulk({ label: 'Submit for review', results: await runBulk(bulkTargets(sel, 'submit_review') as typeof sel, 'submit_review') })}>Submit for review ({can('submit_review')})</Button>
          <Button variant="secondary" disabled={busy || !can('approve')} onClick={async () => setBulk({ label: 'Approve', results: await runBulk(bulkTargets(sel, 'approve') as typeof sel, 'approve') })}>Approve ({can('approve')})</Button>
          <Button variant="secondary" disabled={busy || !can('request_changes')} onClick={() => setPendingChanges(bulkTargets(sel, 'request_changes') as typeof sel)}>Request changes… ({can('request_changes')})</Button>
          <Button variant="secondary" disabled={busy} className="text-danger" onClick={() => { setDeleteErr(null); setPendingDelete(sel); }}>Delete ({sel.length})</Button>
          <button onClick={() => setSelected(new Map())} className="min-h-[36px] px-2 text-text-secondary hover:text-text-primary">Clear</button>
          <span className="w-full text-[11px] text-text-secondary">Approve and request changes apply only to social posts awaiting approval. There is no bulk publish.</span>
        </div>
      )}
      {bulk && <BulkResults label={bulk.label} results={bulk.results} onClose={() => setBulk(null)} />}

      {noDomain ? (
        <div className="mt-4"><EmptyState title="Choose a domain to see its content" teach="Select a domain to view at the top of the app, or set Site to All domains to see everything in your workspace." /></div>
      ) : list.isLoading ? (
        <div className="mt-4"><LoadingState rows={5} /></div>
      ) : list.isError ? (
        <div className="mt-4"><ErrorState error={list.error} onRetry={() => void list.refetch()} /></div>
      ) : items.length === 0 ? (
        <div className="mt-4">
          {first && view !== 'failed' && view !== 'empty' && (first.hidden.failed > 0 || first.hidden.empty > 0) && (
            <p role="status" className="mb-2 text-sm text-text-primary">
              {first.hidden.failed > 0 && <>{first.hidden.failed} {first.hidden.failed === 1 ? 'match' : 'matches'} in Failed — <button className="font-medium text-ai underline" onClick={() => update({ view: 'failed' })}>show</button>. </>}
              {first.hidden.empty > 0 && <>{first.hidden.empty} in Empty drafts — <button className="font-medium text-ai underline" onClick={() => update({ view: 'empty' })}>show</button>.</>}
            </p>
          )}
          <EmptyState title="Nothing matches these filters" teach={fcount || filters.q ? 'Clear a filter or pick another view to see more.' : 'Ask Aura to write a blog, or create a post from Create · AI Studio. New items appear here.'} />
        </div>
      ) : layout === 'table' ? (
        <LibraryTable items={items} h={handlers} allSelected={allSelected} toggleAll={toggleAll} />
      ) : (
        <LibraryCards items={items} h={handlers} />
      )}

      {first && items.length > 0 && (
        <div className="mb-10 mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-text-secondary">
          <span>Showing {items.length} of {first.total}. The address bar keeps your filters, so you can refresh or share this view.</span>
          {list.hasNextPage && <Button variant="secondary" className="min-h-[44px]" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>{list.isFetchingNextPage ? 'Loading…' : 'Load more'}</Button>}
        </div>
      )}
      {first && (first.windowCapped.blogs || first.windowCapped.social) && (
        <p className="mb-6 text-[11px] text-warning">Showing the newest 1,000 {first.windowCapped.blogs && first.windowCapped.social ? 'blogs and posts' : first.windowCapped.blogs ? 'blogs' : 'posts'}. Narrow with filters to reach older items.</p>
      )}

      <Drawer open={autopilotOpen} onClose={() => setAutopilotOpen(false)} title="SEO autopilot" widthClass="w-full max-w-[1000px]">
        <SeoAutopilotPanel expanded />
      </Drawer>

      <LibraryPreviewDrawer item={preview} siteLabel={siteLabel} onClose={() => setPreviewKey(null)} actions={drawerActions} busy={busy} message={drawerMsg} />
      {blogOpen && <BlogEditorDialog id={blogOpen.id} projectId={blogOpen.projectId} onClose={() => setBlogOpen(null)} onChanged={refresh} />}
      {pendingDelete && (
        <ConfirmDeleteDialog
          count={pendingDelete.length}
          title={pendingDelete[0]?.title ?? ''}
          kind={pendingDelete.every((p) => p.kind === 'blog') ? 'blog' : pendingDelete.every((p) => p.kind === 'social') ? 'social' : 'mixed'}
          pending={busy}
          error={deleteErr}
          onCancel={() => setPendingDelete(null)}
          onConfirm={async () => {
            const results = await runBulk(pendingDelete, 'delete');
            if (results.length === 1 && !results[0].ok) { setDeleteErr(errText(null, 'Could not delete this draft. Try again.')); return; }
            setPendingDelete(null);
            if (previewKey && results.some((r) => r.ok && r.key === previewKey)) setPreviewKey(null);
            if (results.length > 1) setBulk({ label: 'Delete', results });
          }}
        />
      )}
      {pendingChanges && (
        <RequestChangesDialog
          count={pendingChanges.length}
          pending={busy}
          onCancel={() => setPendingChanges(null)}
          onConfirm={async (note) => {
            const targets = pendingChanges;
            const results = await runBulk(targets, 'request_changes', note);
            setPendingChanges(null);
            if (targets.length === 1) setDrawerMsg(results[0].ok ? 'Request changes: done.' : `Request changes: not done. ${results[0].message}`);
            else setBulk({ label: 'Request changes', results });
          }}
        />
      )}
    </div>
  );
}
